import React, { useState, useEffect, useRef } from 'react';
import SearchableSelect from './SearchableSelect';
import { enumsApi, citiesApi, specialisationsApi, doctorsApi } from '../services/api';

const blueColor = '#205081';
const lightBlue = '#eaf1fb';
const borderBlue = '#b3c6e7';

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: lightBlue,
    borderColor: borderBlue,
    color: blueColor,
    boxShadow: state.isFocused ? `0 0 0 2px ${borderBlue}` : undefined,
    '&:hover': { borderColor: blueColor },
    minHeight: 40,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: blueColor,
  }),
  input: (provided) => ({
    ...provided,
    color: blueColor,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? blueColor
      : state.isFocused
      ? '#d6e6fa'
      : '#fff',
    color: state.isSelected ? '#fff' : blueColor,
    cursor: 'pointer',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 10,
  }),
};

const DoctorsTable = () => {
  // Состояния для фильтров
  const [fullName, setFullName] = useState('');
  const [city, setCity] = useState([]);
  const [speciality, setSpeciality] = useState([]);
  const [experienceFrom, setExperienceFrom] = useState('');
  const [experienceTo, setExperienceTo] = useState('');
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [ratingPlatforms, setRatingPlatforms] = useState([]); // отдельный селект для рейтинга
  const [platformRatingFilters, setPlatformRatingFilters] = useState([]); // фильтры по платформам для рейтинга
  
  // Состояния для данных
  const [doctors, setDoctors] = useState([]);
  
  // Состояния для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [refreshSpin, setRefreshSpin] = useState(false);
  
  // Состояния для загрузки
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [platformOptions, setPlatformOptions] = useState([]);
  const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });
  const tableRef = useRef(null);

  // Состояния для сортировки
  const [sortField, setSortField] = useState('name'); // 'experience' | 'rating' | 'name' | 'cities' | 'specialisations' | null
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'

  // 1. Новый стейт для хранения открытых строк
  const [expandedRows, setExpandedRows] = useState([]);

  // Новый стейт для контроля ручного обновления
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Добавляю хук для отслеживания ширины экрана
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  // Функция для получения названия платформы
  const getPlatformName = (platformType) => {
    const found = platformOptions.find(opt => Number(opt.value) === Number(platformType));
    return found ? found.label : 'Неизвестно';
  };

  // Функция для получения среднего рейтинга
  const getAverageRating = (rates, digits = 2) => {
    if (!rates || rates.length === 0) return 'Нет данных';
    const avg = rates.reduce((sum, rate) => sum + parseFloat(rate.value), 0) / rates.length;
    return avg.toFixed(digits);
  };

  // Функции загрузки опций для селектов
  const loadPlatformOptions = async (searchParams) => {
    const result = await enumsApi.searchPlatformTypes(searchParams);
    return (result || []).map(p => ({ value: p.value, label: p.name }));
  };

  const loadCityOptions = async (searchParams) => {
    const result = await citiesApi.search(searchParams);
    return result.objects.map(c => ({ 
      value: c.id, 
      label: c.name 
    }));
  };

  const loadSpecialityOptions = async (searchParams) => {
    const result = await specialisationsApi.search(searchParams);
    return result.objects.map(s => ({ 
      value: s.id, 
      label: s.name 
    }));
  };

  // Модифицированная загрузка докторов с сортировкой
  const loadDoctors = async (page = currentPage, size = pageSize) => {
    setLoading(true);
    setRefreshSpin(true);
    setError(null);
    try {
      const searchParams = {
        page: page,
        objectsCount: size,
      };
      if (fullName.trim()) {
        searchParams.searchQuery = fullName.trim();
      }
      if (city.length > 0) {
        searchParams.cityIds = city.map(c => c.value);
      }
      if (speciality.length > 0) {
        searchParams.specialisationIds = speciality.map(s => s.value);
      }
      if (experienceFrom) {
        searchParams.experienceFrom = Number(experienceFrom);
      }
      if (experienceTo) {
        searchParams.experienceTo = Number(experienceTo);
      }
      // Добавляем фильтры по рейтингу для платформ
      if (platformRatingFilters.length > 0) {
        searchParams.platformRatingFilters = platformRatingFilters
          .filter(f => f.platformType !== undefined && f.platformType !== null)
          .map(f => ({
            PlatformType: f.platformType,
            From: f.ratingFrom !== '' ? Number(f.ratingFrom) : undefined,
            To: f.ratingTo !== '' ? Number(f.ratingTo) : undefined
          }));
      }
      if (sortField) {
        searchParams.sortField = sortField;
        searchParams.sortOrder = sortOrder === 'asc' ? 'Ascending' : 'Descending';
      }
      const result = await doctorsApi.getByFilter(searchParams);
      let docs = result.objects || [];
      setDoctors(docs);
      setTotalCount(result.total || 0);
      setTotalPages(Math.ceil((result.total || 0) / size));
    } catch (err) {
      console.error('Failed to load doctors:', err);
      setError('Ошибка загрузки данных докторов');
      setDoctors([]);
    } finally {
      setLoading(false);
      setTimeout(() => setRefreshSpin(false), 600);
    }
  };

  // useEffect для загрузки докторов при изменении фильтров, pageSize, currentPage, sortField, sortOrder
  useEffect(() => {
    loadDoctors(currentPage, pageSize);
    // eslint-disable-next-line
  }, [fullName, city, speciality, experienceFrom, experienceTo, pageSize, currentPage, sortField, sortOrder, ratingPlatforms, platformRatingFilters]);

  useEffect(() => {
    const fetchPlatforms = async () => {
      const result = await enumsApi.searchPlatformTypes({});
      setPlatformOptions((result || []).map(p => ({ value: p.value, label: p.name })));
    };
    fetchPlatforms();
  }, []);

  // Обработчик смены размера страницы
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1); // сбрасываем на первую страницу
  };

  // Обработчик смены страницы
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
      if (tableRef.current) {
        tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Изменяю handleRefresh: теперь он только показывает прелоадер, а данные обновляются после ответа API
  const handleRefresh = async () => {
    setIsRefreshing(true);
    setLoading(true);
    try {
      await loadDoctors(currentPage, pageSize);
    } finally {
      setIsRefreshing(false);
      setLoading(false);
    }
  };

  // Обработчик выбора платформ для рейтинга
  const handleRatingPlatformsChange = (selected) => {
    setRatingPlatforms(selected || []);
    setCurrentPage(1);
    setPlatformRatingFilters((prev) => {
      const selectedIds = (selected || []).map(p => p.value);
      let next = prev.filter(f => selectedIds.includes(f.platformType));
      selectedIds.forEach(id => {
        if (!next.some(f => f.platformType === id)) {
          next.push({ platformType: id, ratingFrom: '', ratingTo: '' });
        }
      });
      return next;
    });
  };

  // Обработчик изменения рейтинга для платформы
  const handlePlatformRatingChange = (platformType, field, value) => {
    setPlatformRatingFilters((prev) => {
      const idx = prev.findIndex(f => f.platformType === platformType);
      if (idx === -1) {
        return [...prev, { platformType, ratingFrom: field === 'from' ? value : '', ratingTo: field === 'to' ? value : '' }];
      } else {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], [field === 'from' ? 'ratingFrom' : 'ratingTo']: value };
        return updated;
      }
    });
    setCurrentPage(1);
  };

  // 2. Функция для переключения раскрытия строки
  const handleRowClick = (id) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  // 3. Компонент для рейтингов по платформам
  const PlatformRatings = ({ rates }) => {
    if (!rates || rates.length === 0) return <div style={{ color: '#888', padding: 8 }}>Нет рейтингов по платформам</div>;
    return (
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 12, padding: 8,
          background: '#f7fafd', borderRadius: 8, border: `1px solid ${borderBlue}`,
          margin: '8px 0',
        }}
      >
        {rates.map(rate => (
          <div key={rate.platformType} style={{ minWidth: 120, flex: '1 1 120px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 500 }}>{getPlatformName(rate.platformType)}:</span>
            <span style={rate.color ? { color: rate.color, fontWeight: 600 } : {}}>{Number(rate.value).toFixed(2)}</span>
          </div>
        ))}
      </div>
    );
  };

  // Обработчик сортировки по колонке
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Маппинг цвета рейтинга (теперь hex приходит с бэка)
  const getRatingColorStyle = (color) => {
    if (color) return { color: color, fontWeight: 'bold' };
    return { color: blueColor };
  };

  useEffect(() => {
    if (tableRef.current) {
      // Триггерим событие resize для корректной работы адаптивных таблиц/скролла
      window.dispatchEvent(new Event('resize'));
    }
  }, [filtersVisible]);

  // Для всех фильтров добавляю сброс страницы на первую при изменении
  const handleFullNameChange = (e) => {
    setFullName(e.target.value);
    setCurrentPage(1);
  };
  const handleCityChange = (val) => {
    setCity(val);
    setCurrentPage(1);
  };
  const handleSpecialityChange = (val) => {
    setSpeciality(val);
    setCurrentPage(1);
  };
  const handleExperienceFromChange = (e) => {
    setExperienceFrom(e.target.value);
    setCurrentPage(1);
  };
  const handleExperienceToChange = (e) => {
    setExperienceTo(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    document.title = 'МИС | Поиск врачей';
  }, []);

  // Добавляю хук для отслеживания ширины экрана
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  return (
    <div className="container" style={{ maxWidth: '85vw', width: '85vw', minWidth: 320, margin: '0 auto', padding: 8, paddingTop: 0 }}>
      {/* Добавляю адаптивный стиль для мобильных устройств */}
      <style>{`
        @media (max-width: 600px) {
          .container {
            max-width: 100vw !important;
            width: 100vw !important;
            min-width: 0 !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
          }
          table.jambo_table {
            width: 100vw !important;
            min-width: 600px;
          }
          .pagination .page-link {
            padding: 2px 8px !important;
            font-size: 13px !important;
            min-width: 28px;
            height: 28px;
            line-height: 22px;
            border-radius: 4px !important;
          }
          .pagination {
            gap: 2px;
          }
        }
      `}</style>
      {/* Карточка с фильтрами */}
      <div className="x_panel" style={{ position: 'relative', marginBottom: 4, padding: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 18, color: blueColor, marginBottom: 8 }}>
          Фильтры поиска докторов
        </div>
        <div className="x_title" style={{ minHeight: 0 }}>
          <button
            onClick={() => setFiltersVisible(!filtersVisible)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              color: blueColor,
              position: 'absolute',
              right: '15px',
              top: '10px',
              padding: '5px',
              borderRadius: '3px',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#d6e6fa'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            {filtersVisible ? '▼' : '▲'}
          </button>
        </div>
        {isRefreshing && (
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}>
            <div className="spinner-border text-primary" role="status" style={{ width: 48, height: 48 }} />
          </div>
        )}
        {filtersVisible && (
          <div className="x_content" style={{ position: 'relative' }}>
            <div className="row form-row" style={{ marginBottom: 24 }}>
              {/* Первая строка: ФИО, специальность, стаж */}
              <div className="col-md-4 form-group">
                <label htmlFor="fullNameInput">ФИО доктора</label>
                <input
                  id="fullNameInput"
                  type="text"
                  className="form-control"
                  value={fullName}
                  onChange={handleFullNameChange}
                  placeholder="Введите ФИО доктора"
                  style={{
                    backgroundColor: lightBlue,
                    borderColor: borderBlue,
                    color: blueColor,
                    minHeight: 40,
                  }}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="specSelect">Специальность</label>
                <SearchableSelect
                  id="specSelect"
                  loadOptions={loadSpecialityOptions}
                  value={speciality}
                  onChange={handleSpecialityChange}
                  isMulti
                  isClearable
                  placeholder="Введите название специальности"
                  styles={customStyles}
                />
              </div>
              <div className="col-md-4 form-group">
                <label>Стаж работы (лет)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="number"
                    className="form-control"
                    value={experienceFrom}
                    onChange={handleExperienceFromChange}
                    placeholder="От"
                    min="0"
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    className="form-control"
                    value={experienceTo}
                    onChange={handleExperienceToChange}
                    placeholder="До"
                    min="0"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
            </div>
            <div className="row form-row" style={{ marginBottom: 24 }}>
              {/* Вторая строка: платформа, город, рейтинг по платформам */}
              <div className="col-md-4 form-group">
                <label htmlFor="citySelect">Город</label>
                <SearchableSelect
                  id="citySelect"
                  loadOptions={loadCityOptions}
                  value={city}
                  onChange={handleCityChange}
                  isMulti
                  isClearable
                  placeholder="Введите название города"
                  styles={customStyles}
                />
              </div>
              <div className="col-md-4 form-group">
                <label>Рейтинг по платформам</label>
                <SearchableSelect
                  id="ratingPlatformSelect"
                  loadOptions={loadPlatformOptions}
                  value={ratingPlatforms}
                  onChange={handleRatingPlatformsChange}
                  isMulti
                  isClearable
                  placeholder="Выберите платформу для фильтра рейтинга"
                  styles={customStyles}
                />
                {ratingPlatforms.length === 0 && <div style={{ color: '#888', fontSize: 13 }}>Сначала выберите платформу</div>}
                {ratingPlatforms.map(p => {
                  const filter = platformRatingFilters.find(f => f.platformType === p.value) || {};
                  return (
                    <div key={p.value} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ minWidth: 120 }}>{p.label}</span>
                      <input
                        type="number"
                        className="form-control"
                        value={filter.ratingFrom || ''}
                        onChange={e => handlePlatformRatingChange(p.value, 'from', e.target.value)}
                        placeholder="От"
                        min="0"
                        max="5"
                        step="0.1"
                        style={{ width: 90 }}
                      />
                      <span>–</span>
                      <input
                        type="number"
                        className="form-control"
                        value={filter.ratingTo || ''}
                        onChange={e => handlePlatformRatingChange(p.value, 'to', e.target.value)}
                        placeholder="До"
                        min="0"
                        max="5"
                        step="0.1"
                        style={{ width: 90 }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <button
              onClick={handleRefresh}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: `1px solid ${borderBlue}`,
                background: lightBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'absolute',
                right: 8,
                bottom: 8,
                zIndex: 2
              }}
              title="Обновить данные"
              disabled={loading}
            >
              <svg
                style={{
                  width: 20,
                  height: 20,
                  transform: refreshSpin ? 'rotate(360deg)' : 'none',
                  transition: 'transform 0.6s cubic-bezier(.4,2.3,.3,1)'
                }}
                viewBox="0 0 24 24"
                fill="none"
                stroke={blueColor}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10M1 14l5.36 5.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Карточка с таблицей */}
      <div className="x_panel" style={{ padding: 8, position: 'relative', marginTop: 8 }}>
        <div className="x_content">
          <div className="table-responsive" ref={tableRef} style={{ position: 'relative' }}>
            <table className="table table-striped jambo_table bulk_action" style={{ marginBottom: 0 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: lightBlue }}>
                <tr className="headings">
                  <th className="column-title" style={{ cursor: 'pointer', width: 220, minWidth: 120 }} onClick={() => handleSort('name')}>
                    ФИО
                    {sortField === 'name' ? (
                      <span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    ) : null}
                  </th>
                  <th className="column-title" style={{ cursor: 'pointer', width: 90, minWidth: 70, textAlign: 'center' }} onClick={() => handleSort('experience')}>
                    Стаж
                    {sortField === 'experience' ? (
                      <span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    ) : null}
                  </th>
                  <th className="column-title" style={{ cursor: 'pointer', width: 90, minWidth: 70, textAlign: 'center' }} onClick={() => handleSort('rating')}>
                    Рейтинг
                    {sortField === 'rating' ? (
                      <span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    ) : null}
                  </th>
                  <th className="column-title" style={{ cursor: 'pointer', width: 160, minWidth: 100 }} onClick={() => handleSort('cities')}>
                    Города
                    {sortField === 'cities' ? (
                      <span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    ) : null}
                  </th>
                  <th className="column-title" style={{ cursor: 'pointer', width: 180, minWidth: 120 }} onClick={() => handleSort('specialisations')}>
                    Специальности
                    {sortField === 'specialisations' ? (
                      <span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    ) : null}
                  </th>
                </tr>
              </thead>
              <tbody>
                {doctors.length === 0 ? (
                  <tr><td colSpan={5} className="text-center">Нет данных</td></tr>
                ) : doctors.map(doc => [
                  <tr key={doc.id} style={{ cursor: 'pointer', background: expandedRows.includes(doc.id) ? '#f7fafd' : undefined }} onClick={() => handleRowClick(doc.id)}>
                    <td>{doc.name || 'Не указано'}</td>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>{doc.experience ? `${doc.experience} лет` : 'Не указан'}</td>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      {doc.averageRating !== undefined && doc.averageRating !== null ? (
                        <span style={getRatingColorStyle(doc.ratingColor)}>{doc.averageRating.toFixed(2)}</span>
                      ) : (
                        getAverageRating(doc.rates, 2)
                      )}
                    </td>
                    <td>{doc.cities && doc.cities.length > 0 ? doc.cities.map(city => city.name || city.id).join(', ') : '—'}</td>
                    <td>{doc.specialisations.map(spec => spec.name).join(', ')}</td>
                  </tr>,
                  expandedRows.includes(doc.id) && (
                    <tr key={doc.id + '-expanded'}>
                      <td colSpan={5} style={{ padding: 0, background: '#f7fafd' }}>
                        <PlatformRatings rates={doc.rates} />
                      </td>
                    </tr>
                  )
                ])}
              </tbody>
            </table>
            {loading && (
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}>
                <div className="spinner-border text-primary" role="status" style={{ width: 48, height: 48 }} />
              </div>
            )}
          </div>
          <div className="row" style={{ marginTop: 20, alignItems: 'center', justifyContent: isMobile ? 'center' : 'space-between' }}>
            <div className="col-md-6" style={{ display: isMobile ? 'none' : 'flex', alignItems: 'center', gap: 12 }}>
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                style={{ minWidth: 70, height: 36, borderRadius: 6, border: `1px solid ${borderBlue}`, color: blueColor, background: lightBlue }}
                title="Элементов на странице"
                disabled={loading}
              >
                {[10, 20, 50, 100].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span style={{ color: blueColor, fontSize: 14 }}>
                Показано {doctors.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} - {doctors.length > 0 ? ((currentPage - 1) * pageSize) + doctors.length : 0} из {totalCount} записей
              </span>
            </div>
            <div className="col-md-6" style={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end' }}>
              {totalPages > 1 && (
                <nav aria-label="Навигация по страницам">
                  <ul className="pagination justify-content-end" style={{ margin: 0 }}>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{ color: blueColor, borderColor: borderBlue }}
                      >
                        {isMobile ? '←' : 'Предыдущая'}
                      </button>
                    </li>
                    {(() => {
                      const pages = [];
                      if (totalPages <= 7) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        if (currentPage <= 4) {
                          pages.push(1,2,3,4,5,'...',totalPages);
                        } else if (currentPage >= totalPages - 3) {
                          pages.push(1,'...',totalPages-4,totalPages-3,totalPages-2,totalPages-1,totalPages);
                        } else {
                          pages.push(1,'...',currentPage-1,currentPage,currentPage+1,'...',totalPages);
                        }
                      }
                      return pages.map((pageNum, idx) =>
                        pageNum === '...'
                          ? <li key={"ellipsis-bottom-"+idx} className="page-item disabled"><span className="page-link">...</span></li>
                          : <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => handlePageChange(pageNum)}
                                style={{ 
                                  color: currentPage === pageNum ? '#fff' : blueColor, 
                                  backgroundColor: currentPage === pageNum ? blueColor : 'transparent',
                                  borderColor: borderBlue 
                                }}
                              >
                                {pageNum}
                              </button>
                            </li>
                      );
                    })()}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button 
                        className="page-link" 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{ color: blueColor, borderColor: borderBlue }}
                      >
                        {isMobile ? '→' : 'Следующая'}
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
        </div>
      </div>
      <div style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        marginTop: 24,
        marginBottom: 40,
        color: '#205081',
        background: 'rgba(255,255,255,0.65)',
        borderRadius: 6,
        padding: '2px 14px',
        fontSize: 13,
        fontWeight: 400,
        opacity: 0.85,
        letterSpacing: 0.2,
        pointerEvents: 'none',
        userSelect: 'none',
        maxWidth: 320,
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        МИС | Space App | 1.0.0
      </div>
    </div>
  );
};

export default DoctorsTable; 